import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { UserService } from './api/user/user.service';
import { ColumnConfig } from '@models/user';
import { ColumnOption, ColumnToggleOptions } from '@models/shared.model';
import { SubscriptionList, SubscriptionListType } from '@helpers/subscription';

@Injectable({
    providedIn: 'root',
})
export class ColumnOptionsService {
    private readonly columnOptions = new BehaviorSubject<
        ColumnToggleOptions<string>
    >({
        lastToggled: null,
        hiddenColumns: [],
    });
    private readonly nestedColumnOptions = new BehaviorSubject<{
        hiddenColumns: string[];
    }>({ hiddenColumns: [] });
    private readonly discColumnOptions = new BehaviorSubject<
        ColumnToggleOptions<string>
    >({
        lastToggled: null,
        hiddenColumns: [],
    });

    columnOptions$ = this.columnOptions.asObservable();
    nestedColumnOptions$ = this.nestedColumnOptions.asObservable();
    discColumnOptions$ = this.discColumnOptions.asObservable();

    private readonly userService = inject(UserService);
    private _subscriptions = new SubscriptionList() as SubscriptionListType;

    updateColumnOptions(option: Pick<ColumnOption<string>, 'id' | 'checked'>) {
        this.columnOptions.next({
            lastToggled: option,
            hiddenColumns: this.computeHiddenColumns(
                this.columnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    updateNestedColumnOptions(
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ) {
        this.nestedColumnOptions.next({
            hiddenColumns: this.computeHiddenColumns(
                this.nestedColumnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    updateDiscColumnOptions(
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ) {
        this.discColumnOptions.next({
            lastToggled: option,
            hiddenColumns: this.computeHiddenColumns(
                this.discColumnOptions.value.hiddenColumns,
                option,
            ),
        });

        this.saveColumnOptions();
    }

    /**
     * Add or remove a column id from the hidden list based on the user's checked
     * state (true = show/remove, false = hide/add). Matching is case-insensitive
     * because restored ids are lowercased (see syncColumnConfig) while option ids
     * keep their original camelCase. A case-sensitive compare would fail to remove
     * a restored id (e.g. 'documenttype' vs 'documentType'), leaving a duplicate
     * entry that both reverts the column on refresh and can trigger a backend
     * unique-key (DbUpdateException) error.
     */
    private computeHiddenColumns(
        current: string[],
        option: Pick<ColumnOption<string>, 'id' | 'checked'>,
    ): string[] {
        const id = String(option.id).toLowerCase();
        const withoutId = current.filter((h) => h.toLowerCase() !== id);
        return option.checked ? withoutId : [...withoutId, option.id];
    }

    syncColumnConfig(config: ColumnConfig) {
        this.columnOptions.next({
            lastToggled: null,
            hiddenColumns: config.documentView.hiddenColumns.map((id) =>
                id.toLowerCase(),
            ),
        });
        this.nestedColumnOptions.next({
            hiddenColumns: config.documentView.nestedHiddenColumns.map((id) =>
                id.toLowerCase(),
            ),
        });
        this.discColumnOptions.next({
            lastToggled: null,
            hiddenColumns: config.discrepancyView.hiddenColumns.map((id) =>
                id.toLowerCase(),
            ),
        });
    }

    private saveColumnOptions() {
        const config: ColumnConfig = {
            documentView: {
                hiddenColumns: this.columnOptions.getValue().hiddenColumns,
                nestedHiddenColumns:
                    this.nestedColumnOptions.getValue().hiddenColumns,
            },
            discrepancyView: {
                hiddenColumns: this.discColumnOptions.getValue().hiddenColumns,
            },
        };

        this._subscriptions.unsubscribeSafe('save-column-config');
        this._subscriptions['save-column-config'] = this.userService
            .saveColumnConfig(config)
            .subscribe();
    }
}
